import React, { useState, useCallback, useEffect } from "react";
import ORCLogo from "../assets/ORClogo.png";

const Solitaire = () => {
  const SUITS = ["♠", "♥", "♦", "♣"];
  const RANKS = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];
  const RANK_VALUES = { A: 1, J: 11, Q: 12, K: 13 };

  const [gameState, setGameState] = useState(null);
  const [moves, setMoves] = useState(0);
  const [won, setWon] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [maxRecycles, setMaxRecycles] = useState(null);
  const [recycleCount, setRecycleCount] = useState(0);
  const [showOptions, setShowOptions] = useState(false);
  const containerRef = React.useRef(null);

  // Set up global mouse event listeners
  useEffect(() => {
    if (!containerRef.current || !dragging) return;

    const handleMouseMove = (e) => {
      const dragGhost = document.getElementById('drag-ghost');
      if (dragGhost) {
        dragGhost.style.left = `${e.clientX - dragging.offset.x}px`;
        dragGhost.style.top = `${e.clientY - dragging.offset.y}px`;
      }
    };

    const handleMouseUp = (e) => {
      if (!dragging) return;
      
      const { card, fromLocation, fromIndex, cardIndex } = dragging;
      const dragGhost = document.getElementById('drag-ghost');
      if (dragGhost) dragGhost.remove();

      // Find drop target by walking up the DOM tree from the element at drop point
      let elemBelow = document.elementFromPoint(e.clientX, e.clientY);
      let dropTarget = null;
      
      // Walk up the tree to find an element with data-drop-target
      while (elemBelow && elemBelow !== containerRef.current && elemBelow !== dragGhost) {
        if (elemBelow.dataset?.dropTarget) {
          dropTarget = elemBelow.dataset.dropTarget;
          break;
        }
        elemBelow = elemBelow.parentElement;
      }

      // Only try move if we found a valid drop target
      if (dropTarget) {
        const [toLocation, toIndex] = dropTarget.split('-');
        tryMoveCard(card, fromLocation, parseInt(fromIndex), toLocation, parseInt(toIndex), cardIndex);
      }

      setDragging(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);

  // Initialize game
  const initGame = useCallback(() => {
    // Create deck
    const deck = [];
    for (let suit of SUITS) {
      for (let rank of RANKS) {
        deck.push({ suit, rank, faceUp: false, id: Math.random() });
      }
    }

    // Shuffle
    for (let i = deck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [deck[i], deck[j]] = [deck[j], deck[i]];
    }

    // Deal
    const tableaus = Array(7).fill(null).map(() => []);
    let idx = 0;
    for (let col = 0; col < 7; col++) {
      for (let row = 0; row <= col; row++) {
        tableaus[col].push({
          ...deck[idx],
          faceUp: row === col,
        });
        idx++;
      }
    }

    const newState = {
      stock: deck.slice(idx),
      waste: [],
      foundations: [[], [], [], []],
      tableaus,
    };
    setGameState(newState);
    setMoves(0);
    setWon(false);
    setRecycleCount(0);
    setUndoStack([newState]);
  }, []);

  // Initialize on mount
  useEffect(() => {
    initGame();
  }, [initGame]);

  // Check for win condition
  useEffect(() => {
    if (gameState && gameState.foundations.every(f => f.length === 13)) {
      setWon(true);
    }
  }, [gameState]);

  if (!gameState) return <div>Loading...</div>;

  const getRank = (rank) => RANK_VALUES[rank] || parseInt(rank);

  const isRed = (suit) => suit === "♥" || suit === "♦";

  const canMove = (card, pile) => {
    if (!pile || pile.length === 0) return getRank(card.rank) === 13;
    const top = pile[pile.length - 1];
    if (!top.faceUp) return false;
    const cardIsRed = isRed(card.suit);
    const topIsRed = isRed(top.suit);
    return cardIsRed !== topIsRed && getRank(card.rank) === getRank(top.rank) - 1;
  };

  const canMoveToFoundation = (card, foundationIndex) => {
    if (!gameState) return false;
    const foundation = gameState.foundations[foundationIndex];
    // Aces can go to ANY empty foundation
    if (getRank(card.rank) === 1 && foundation.length === 0) return true;
    // Foundation must not be empty for non-aces
    if (foundation.length === 0) return false;
    // Get the top card of the foundation
    const top = foundation[foundation.length - 1];
    // Card must match the suit of foundation AND be next in sequence
    return card.suit === top.suit && getRank(card.rank) === getRank(top.rank) + 1;
  };

  const tryAutoMove = (newState) => {
    // Try to move waste or top tableau cards to foundations automatically
    const { waste, tableaus, foundations } = newState;
    let moved = false;

    // Try waste card
    if (waste.length > 0) {
      const card = waste[waste.length - 1];
      for (let i = 0; i < 4; i++) {
        if (canMoveToFoundation(card, i)) {
          const newWaste = waste.slice(0, -1);
          const newFoundations = foundations.map(f => [...f]);
          newFoundations[i].push(card);
          setGameState({ ...newState, waste: newWaste, foundations: newFoundations });
          setMoves(prev => prev + 1);
          return true;
        }
      }
    }

    // Try tableau cards
    for (let col = 0; col < 7; col++) {
      if (tableaus[col].length > 0) {
        const card = tableaus[col][tableaus[col].length - 1];
        if (!card.faceUp) break;
        for (let i = 0; i < 4; i++) {
          if (canMoveToFoundation(card, i)) {
            const newTableaus = tableaus.map(t => [...t]);
            newTableaus[col].pop();
            if (newTableaus[col].length > 0) {
              newTableaus[col][newTableaus[col].length - 1].faceUp = true;
            }
            const newFoundations = foundations.map(f => [...f]);
            newFoundations[i].push(card);
            setGameState({ ...newState, tableaus: newTableaus, foundations: newFoundations });
            setMoves(prev => prev + 1);
            return true;
          }
        }
      }
    }
    return false;
  };

  const handleStockClick = () => {
    if (!gameState) return;
    const { stock, waste } = gameState;
    
    let newState;
    if (stock.length === 0) {
      if (waste.length === 0) return;
      if (maxRecycles !== null && recycleCount >= maxRecycles) {
        setWon(false);
        alert(`Game Over! You have used all ${maxRecycles} recycles.`);
        return;
      }
      newState = { ...gameState, stock: waste.map(c => ({ ...c, faceUp: false })).reverse(), waste: [] };
      setRecycleCount(prev => prev + 1);
    } else {
      newState = {
        ...gameState,
        stock: stock.slice(0, -1),
        waste: [...waste, { ...stock[stock.length - 1], faceUp: true }],
      };
    }
    setUndoStack([...undoStack, newState]);
    setGameState(newState);
    setMoves(prev => prev + 1);
  };

  const getSubCards = (allCards, location, index) => {
    // Get cards below the selected card in same column
    if (location === "tableau") {
      return allCards.slice(index + 1);
    }
    return [];
  };

  const getAvailableDestinations = (cardIndex, card, isFirst = false) => {
    if (!gameState) return [];
    const { waste, foundations, tableaus } = gameState;
    const destinations = [];

    // Check foundations (for Aces or sequences)
    if (getRank(card.rank) === 1) {
      // Aces can go anywhere
      for (let i = 0; i < 4; i++) {
        if (gameState.foundations[i].length === 0) {
          destinations.push({ type: "foundation", index: i });
          if (isFirst) return destinations;
        }
      }
    }

    // Other cards go to matching suit
    for (let i = 0; i < 4; i++) {
      const l = gameState.foundations[i].length;
      if (l + 1 === getRank(card.rank)) {
        const lastType = gameState.foundations[i].length > 0 
          ? gameState.foundations[i][gameState.foundations[i].length - 1].suit
          : null;
        if (lastType === card.suit || lastType === null) {
          destinations.push({ type: "foundation", index: i });
          if (isFirst) return destinations;
        }
      }
    }

    // Check tableau piles
    for (let i = 0; i < 7; i++) {
      const last = tableaus[i].length > 0 ? tableaus[i][tableaus[i].length - 1] : null;
      if (last !== null && canMove(card, tableaus[i])) {
        destinations.push({ type: "tableau", index: i });
        if (isFirst) return destinations;
      } else if (last === null && getRank(card.rank) === 13) {
        // Empty tableau accepts only Kings
        destinations.push({ type: "tableau", index: i });
        if (isFirst) return destinations;
      }
    }

    return destinations;
  };

  const tryMoveCard = (card, fromLocation, fromIndex, toLocation, toIndex, cardIndex = null) => {
    if (!gameState || !card) return false;
    const { waste, foundations, tableaus } = gameState;

    // Verify card still exists and find its position
    let cardStillValid = false;
    let cardPosition = -1;
    
    if (fromLocation === "waste" && waste.length > 0 && waste[waste.length - 1].id === card.id) {
      cardStillValid = true;
      cardPosition = 0;
    } else if (fromLocation === "tableau") {
      const tableau = tableaus[fromIndex];
      if (tableau) {
        if (cardIndex !== null && tableau[cardIndex]?.id === card.id) {
          // If cardIndex provided, use it
          cardStillValid = true;
          cardPosition = cardIndex;
        } else {
          // Fall back to finding by ID if cardIndex not provided
          cardPosition = tableau.findIndex(c => c.id === card.id);
          if (cardPosition !== -1) {
            cardStillValid = true;
          }
        }
      }
    }
    if (!cardStillValid) return false;

    // Try foundation move (only single cards)
    if (toLocation === "foundation") {
      // Only move single cards to foundation, not stacks
      if (fromLocation === "tableau" && cardPosition !== tableaus[fromIndex].length - 1) {
        return false; // Can only move top card to foundation
      }
      
      for (let i = 0; i < 4; i++) {
        if (canMoveToFoundation(card, i)) {
          const newFoundations = foundations.map(f => [...f]);
          newFoundations[i].push(card);

          let newState;
          if (fromLocation === "waste") {
            const newWaste = waste.slice(0, -1);
            newState = { ...gameState, foundations: newFoundations, waste: newWaste };
          } else if (fromLocation === "tableau") {
            const newTableaus = tableaus.map(t => [...t]);
            newTableaus[fromIndex].pop();
            if (newTableaus[fromIndex].length > 0) {
              newTableaus[fromIndex][newTableaus[fromIndex].length - 1].faceUp = true;
            }
            newState = { ...gameState, foundations: newFoundations, tableaus: newTableaus };
          } else {
            return false;
          }

          setUndoStack([...undoStack, newState]);
          setGameState(newState);
          setMoves(prev => prev + 1);
          return true;
        }
      }
      return false;
    }

    // Try tableau move (supports card stacks)
    if (toLocation === "tableau") {
      const fromIsTableau = fromLocation === "tableau";
      
      // Don't allow moving to the same column
      if (fromIsTableau && fromIndex === toIndex) return false;
      
      // Get all cards to move (from clicked card to bottom of column)
      let cardsToMove = [];
      if (fromLocation === "tableau") {
        cardsToMove = tableaus[fromIndex].slice(cardPosition);
      } else if (fromLocation === "waste") {
        cardsToMove = [card];
      }
      
      if (cardsToMove.length === 0) return false;
      
      // Check if we can move the first card (top of stack)
      const destTableau = tableaus[toIndex];
      if (!canMove(cardsToMove[0], destTableau)) return false;
      
      const newTableaus = tableaus.map(t => [...t]);
      
      // Remove cards from source
      if (fromLocation === "tableau") {
        newTableaus[fromIndex] = newTableaus[fromIndex].slice(0, cardPosition);
        if (newTableaus[fromIndex].length > 0 && !newTableaus[fromIndex][newTableaus[fromIndex].length - 1].faceUp) {
          newTableaus[fromIndex][newTableaus[fromIndex].length - 1].faceUp = true;
        }
      } else if (fromLocation === "waste") {
        newTableaus[toIndex].push(...cardsToMove);
        const newWaste = gameState.waste.slice(0, -1);
        const newState = { ...gameState, waste: newWaste, tableaus: newTableaus };
        setUndoStack([...undoStack, newState]);
        setGameState(newState);
        setMoves(prev => prev + 1);
        return true;
      }
      
      // Add cards to destination
      newTableaus[toIndex].push(...cardsToMove);

      const newState = { ...gameState, tableaus: newTableaus };
      setUndoStack([...undoStack, newState]);
      setGameState(newState);
      setMoves(prev => prev + 1);
      return true;
    }

    return false;
  };

  const captureMove = (card, fromLocation, fromIndex, cardIndex = null) => (e) => {
    if (!card?.faceUp) return;
    e.preventDefault();
    e.stopPropagation();

    const element = e.currentTarget;
    const rect = element.getBoundingClientRect();
    
    // Create a ghost/clone element for dragging
    const ghost = element.cloneNode(true);
    ghost.id = 'drag-ghost';
    ghost.style.position = 'fixed';
    ghost.style.zIndex = '10000';
    ghost.style.pointerEvents = 'none';
    ghost.style.opacity = '0.9';
    ghost.style.cursor = 'grabbing';
    ghost.style.boxShadow = '0 5px 20px rgba(0,0,0,0.3)';
    ghost.style.transform = 'scale(1.05)';
    // Set initial position BEFORE appending to avoid upper-left flash
    ghost.style.left = `${e.clientX - (rect.left - rect.left)}px`;
    ghost.style.top = `${e.clientY - (rect.top - rect.top)}px`;
    document.body.appendChild(ghost);
    
    setDragging({
      card,
      fromLocation,
      fromIndex,
      cardIndex: cardIndex !== null ? cardIndex : 0, // For tableau, this is the card's position in the column
      element,
      offset: {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      },
    });
  };

  const handleUndo = () => {
    if (undoStack.length > 1) {
      const newStack = undoStack.slice(0, -1);
      setUndoStack(newStack);
      setGameState(newStack[newStack.length - 1]);
      setMoves(prev => Math.max(0, prev - 1));
    }
  };

  const Card = ({ card, selected: isSelected, inStack = false }) => {
    if (!card) return null;
    
    return (
      <div
        style={{
          width: "60px",
          height: "80px",
          border: "2px solid #333",
          borderRadius: "4px",
          background: card.faceUp ? "#fff" : "#0066cc",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: card.faceUp ? (isRed(card.suit) ? "#c00" : "#000") : "#fff",
          fontWeight: "bold",
          fontSize: "10px",
          boxShadow: isSelected ? "0 0 15px #ffd700" : "0 2px 4px rgba(0,0,0,0.2)",
          transition: "all 0.15s",
          userSelect: "none",
          position: "relative",
          padding: "2px",
        }}
      >
        {card.faceUp ? (
          <>
            {/* Upper left corner */}
            <div style={{ position: "absolute", top: "2px", left: "2px", lineHeight: "1", textAlign: "center", pointerEvents: "none" }}>
              <div style={{ fontSize: "11px" }}>{card.rank}</div>
              <div style={{ fontSize: "11px" }}>{card.suit}</div>
            </div>
            {/* Center (for visual balance) */}
            <div style={{ fontSize: "16px", pointerEvents: "none" }}>{card.suit}</div>
            {/* Lower right corner (upside down) */}
            <div style={{ position: "absolute", bottom: "2px", right: "2px", lineHeight: "1", textAlign: "center", transform: "rotate(180deg)", pointerEvents: "none" }}>
              <div style={{ fontSize: "11px" }}>{card.rank}</div>
              <div style={{ fontSize: "11px" }}>{card.suit}</div>
            </div>
          </>
        ) : (
          <span style={{ fontSize: "20px", pointerEvents: "none" }}>🂠</span>
        )}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      style={{
        padding: "16px",
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        background: "#f0f8f0",
        minHeight: "100vh",
        borderRadius: "8px",
      }}
    >
      {/* Win Screen */}
      {won && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "40px",
              borderRadius: "12px",
              textAlign: "center",
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "20px" }}>🎉</div>
            <h1 style={{ color: "#333", marginBottom: "20px" }}>You Won!</h1>
            <p style={{ color: "#666", marginBottom: "20px", fontSize: "16px" }}>
              Moves: {moves}
            </p>
            <button
              onClick={initGame}
              style={{
                padding: "12px 24px",
                background: "#0066cc",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              Play Again
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <img
            src={ORCLogo}
            alt="Outdoorec Logo"
            width={120}
            height={37}
            style={{ maxWidth: "120px", maxHeight: "37px", width: "120px", height: "37px" }}
            className="object-contain"
          />
          <h1 style={{ color: "#333", marginBottom: 0, marginTop: "0", margin: 0 }}>Solitaire</h1>
        </div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <span style={{ fontSize: "16px", fontWeight: "bold", color: "#333" }}>
            Moves: {moves}
          </span>
          <button
            onClick={() => setShowOptions(!showOptions)}
            style={{
              padding: "8px 16px",
              background: "#0066cc",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            Options
          </button>
          <button
            onClick={handleUndo}
            disabled={undoStack.length <= 1}
            style={{
              padding: "8px 16px",
              background: undoStack.length <= 1 ? "#ccc" : "#ff9500",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: undoStack.length <= 1 ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            Undo
          </button>
        </div>
      </div>

      {/* Stock, Waste, Foundations */}
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "24px",
          alignItems: "flex-start",
        }}
      >
        <div>
          <div style={{ fontSize: "12px", marginBottom: "6px", color: "#666" }}>Stock</div>
          <div
            onClick={handleStockClick}
            style={{
              width: "60px",
              height: "80px",
              border: "2px dashed #999",
              borderRadius: "4px",
              background: "#e8f4e8",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "20px",
              pointerEvents: "auto",
              userSelect: "none",
            }}
          >
            {gameState?.stock?.length > 0 ? "🂠" : "—"}
          </div>
          <div style={{ fontSize: "11px", marginTop: "4px", color: "#666", textAlign: "center" }}>
            {gameState?.stock?.length || 0}
          </div>
        </div>

        <div>
          <div style={{ fontSize: "12px", marginBottom: "6px", color: "#666" }}>Waste</div>
          {gameState?.waste?.length > 0 ? (
            <div
              onMouseDown={(e) => {
                const card = gameState.waste[gameState.waste.length - 1];
                captureMove(card, "waste", 0)(e);
              }}
              data-drop-target="foundation-0"
              style={{
                cursor: "grab",
                pointerEvents: "auto",
                userSelect: "none",
                display: "inline-block",
              }}
            >
              <Card
                card={gameState.waste[gameState.waste.length - 1]}
                selected={dragging?.fromLocation === "waste"}
              />
            </div>
          ) : (
            <div
              style={{
                width: "60px",
                height: "80px",
                border: "2px dashed #bbb",
                borderRadius: "4px",
                background: "#f5f5f5",
              }}
            />
          )}
        </div>

        {/* Foundations */}
        {gameState?.foundations?.map((foundation, i) => (
          <div key={i}>
            <div style={{ fontSize: "12px", marginBottom: "6px", color: "#666", minHeight: "18px" }}>
              {foundation.length > 0 && foundation[foundation.length - 1].suit}
            </div>
            {foundation.length > 0 ? (
              <div
                data-drop-target={`foundation-${i}`}
                style={{ cursor: "pointer" }}
              >
                <Card 
                  card={foundation[foundation.length - 1]} 
                  selected={false} 
                />
              </div>
            ) : (
              <div
                data-drop-target={`foundation-${i}`}
                style={{
                  width: "60px",
                  height: "80px",
                  border: "2px dashed #999",
                  borderRadius: "4px",
                  background: "#f0f0f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#ccc",
                  fontSize: "24px",
                  cursor: "pointer",
                }}
              >
                A
              </div>
            )}
            <div style={{ fontSize: "11px", marginTop: "4px", color: "#666", textAlign: "center" }}>
              {foundation.length}/13
            </div>
          </div>
        ))}
      </div>

      {/* Tableaus */}
      <div
        style={{
          background: "#1a4d2e",
          padding: "16px",
          borderRadius: "8px",
          marginTop: "20px",
          minWidth: "fit-content",
          overflowX: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "space-between",
            minWidth: "calc(7 * 82px + 6 * 12px + 32px)",
          }}
        >
          {gameState?.tableaus?.map((tableau, i) => (
            <div
              key={i}
              data-drop-target={`tableau-${i}`}
              style={{
                flex: 1,
                minWidth: "70px",
                minHeight: "500px",
                border: "2px solid #999",
                borderRadius: "6px",
                background: "transparent",
                padding: "6px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              justifyContent: "flex-start",
              position: "relative",
              overflow: "visible",
            }}
          >
            {tableau.length === 0 ? (
              <div
                style={{
                  color: "#aaa",
                  marginTop: "12px",
                  fontSize: "24px",
                }}
              >
                —
              </div>
            ) : (
              <div 
                style={{ 
                  position: "relative", 
                  width: "65px", 
                  height: "400px",
                  overflow: "visible",
                }}
              >
                {tableau.map((card, cardIdx) => {
                  const topOffset = cardIdx * 40;
                  
                  return (
                    <div
                      key={`${i}-${cardIdx}-${card.id}`}
                      onMouseDown={(e) => captureMove(card, "tableau", i, cardIdx)(e)}
                      data-drop-target={`tableau-${i}`}
                      style={{
                        position: "absolute",
                        top: `${topOffset}px`,
                        left: 0,
                        width: "60px",
                        height: "80px",
                        zIndex: cardIdx,
                        cursor: card.faceUp ? "grab" : "default",
                        border: "2px solid #333",
                        borderRadius: "4px",
                        background: card.faceUp ? "#fff" : "#0066cc",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        color: card.faceUp ? (isRed(card.suit) ? "#c00" : "#000") : "#fff",
                        fontWeight: "bold",
                        fontSize: "12px",
                        boxShadow: dragging?.fromLocation === "tableau" && dragging?.fromIndex === i ? "0 0 15px #ffd700" : "0 2px 4px rgba(0,0,0,0.2)",
                        transition: "all 0.15s",
                        userSelect: "none",
                        padding: "2px",
                      }}
                    >
                      {card.faceUp ? (
                        <>
                          <div style={{ position: "absolute", top: "2px", left: "2px", lineHeight: "1", textAlign: "center", pointerEvents: "none" }}>
                            <div style={{ fontSize: "11px" }}>{card.rank}</div>
                            <div style={{ fontSize: "11px" }}>{card.suit}</div>
                          </div>
                          <div style={{ fontSize: "16px", pointerEvents: "none" }}>{card.suit}</div>
                          <div style={{ position: "absolute", bottom: "2px", right: "2px", lineHeight: "1", textAlign: "center", transform: "rotate(180deg)", pointerEvents: "none" }}>
                            <div style={{ fontSize: "11px" }}>{card.rank}</div>
                            <div style={{ fontSize: "11px" }}>{card.suit}</div>
                          </div>
                        </>
                      ) : (
                        <span style={{ fontSize: "20px", pointerEvents: "none" }}>🂠</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        </div>
      </div>

      {/* Instructions */}
      <div
        style={{
          marginTop: "24px",
          padding: "12px",
          background: "#e8f4e8",
          borderRadius: "6px",
          fontSize: "13px",
          color: "#666",
          lineHeight: "1.6",
        }}
      >
        <strong>Rules:</strong> Move cards to the foundations by suit in ascending order (A→K).
        Build tableaus in descending order with alternating colors. Empty columns accept only Kings.
      </div>

      {/* Options Dialog - Windows Style */}
      {showOptions && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "#dfdfdf",
            border: "2px solid",
            borderColor: "#dfdfdf #808080 #808080 #dfdfdf",
            boxShadow: "1px 1px 0 #ffffff inset, -1px -1px 0 #0a0a0a inset",
            zIndex: 10000,
            minWidth: "350px",
            fontFamily: "MS Sans Serif, Arial, sans-serif",
            fontSize: "11px",
          }}
        >
          {/* Title Bar */}
          <div
            style={{
              background: "linear-gradient(90deg, #000080 0%, #1084d7 100%)",
              color: "white",
              padding: "2px 2px 2px 4px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontWeight: "bold",
              fontSize: "11px",
              cursor: "default",
            }}
          >
            <span>Game Options</span>
            <button
              onClick={() => setShowOptions(false)}
              style={{
                width: "16px",
                height: "14px",
                background: "#dfdfdf",
                border: "1px solid",
                borderColor: "#dfdfdf #808080 #808080 #dfdfdf",
                color: "black",
                fontWeight: "bold",
                fontSize: "10px",
                cursor: "pointer",
                padding: "0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div style={{ padding: "12px", background: "#dfdfdf" }}>
            {/* New Game */}
            <div style={{ marginBottom: "16px" }}>
              <button
                onClick={() => {
                  initGame();
                  setShowOptions(false);
                }}
                style={{
                  width: "100%",
                  padding: "6px 12px",
                  background: "#dfdfdf",
                  border: "2px solid",
                  borderColor: "#dfdfdf #808080 #808080 #dfdfdf",
                  cursor: "pointer",
                  fontSize: "11px",
                  fontWeight: "bold",
                  fontFamily: "MS Sans Serif, Arial, sans-serif",
                }}
              >
                New Game
              </button>
            </div>

            {/* Max Recycles */}
            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: "bold" }}>Max Recycles:</label>
              <select
                value={maxRecycles === null ? "unlimited" : maxRecycles}
                onChange={(e) => {
                  const value = e.target.value === "unlimited" ? null : parseInt(e.target.value);
                  setMaxRecycles(value);
                  setRecycleCount(0);
                }}
                style={{
                  width: "100%",
                  padding: "4px",
                  border: "2px solid",
                  borderColor: "#808080 #dfdfdf #dfdfdf #808080",
                  background: "white",
                  fontSize: "11px",
                  fontFamily: "MS Sans Serif, Arial, sans-serif",
                  boxSizing: "border-box",
                }}
              >
                <option value="unlimited">Unlimited</option>
                <option value="1">1</option>
                <option value="3">3</option>
                <option value="5">5</option>
              </select>
              {maxRecycles !== null && (
                <div style={{ marginTop: "4px", fontSize: "11px", color: "#666" }}>
                  Current: {recycleCount}/{maxRecycles}
                </div>
              )}
            </div>

            {/* Divider */}
            <div style={{ borderTop: "1px solid #c0c0c0", margin: "12px 0" }} />

            {/* Close Button */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px" }}>
              <button
                onClick={() => setShowOptions(false)}
                style={{
                  padding: "4px 16px",
                  background: "#dfdfdf",
                  border: "2px solid",
                  borderColor: "#dfdfdf #808080 #808080 #dfdfdf",
                  cursor: "pointer",
                  fontSize: "11px",
                  fontFamily: "MS Sans Serif, Arial, sans-serif",
                  minWidth: "70px",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Solitaire;

